import { useEffect } from 'react';

/**
 * Lightweight lerp-based smooth scroll (the "slow, stretchy" feel from
 * editorial sites like wolverineworldwide.com). No dependencies.
 *
 * - Hijacks wheel, eases the real scroll position toward a target each frame.
 * - Skips touch devices (native momentum is already good) and reduced-motion.
 * - Leaves interactive/scrollable areas (the map) on native scroll via opt-out.
 */
export function useSmoothScroll(
  { ease = 0.085, optOut = '.evmap, .leaflet-container, [data-native-scroll]' } = {},
): void {
  useEffect(() => {
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqTouch = window.matchMedia('(hover: none), (pointer: coarse)');
    if (mqReduce.matches || mqTouch.matches) return;

    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    let current = window.scrollY;
    let target = window.scrollY;
    let running = false;
    let raf = 0;

    const maxScroll = (): number =>
      Math.max(0, html.scrollHeight - window.innerHeight);
    const clamp = (v: number): number => Math.max(0, Math.min(v, maxScroll()));

    const loop = (): void => {
      current += (target - current) * ease;
      if (Math.abs(target - current) < 0.4) {
        current = target;
        window.scrollTo(0, Math.round(current));
        running = false;
        return;
      }
      window.scrollTo(0, current);
      raf = requestAnimationFrame(loop);
    };
    const start = (): void => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(loop);
      }
    };

    const onWheel = (e: WheelEvent): void => {
      if (e.ctrlKey) return; // pinch-zoom
      const t = e.target as HTMLElement | null;
      if (t && t.closest(optOut)) return; // let the map etc. scroll natively
      e.preventDefault();
      if (!running) {
        current = window.scrollY;
        target = window.scrollY;
      }
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      target = clamp(target + delta);
      start();
    };

    // Keep the target synced when scroll happens by other means (anchor jump,
    // keyboard, scrollbar drag) so the next wheel doesn't snap.
    const onScroll = (): void => {
      if (!running) {
        current = window.scrollY;
        target = window.scrollY;
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
      html.style.scrollBehavior = prevBehavior;
    };
  }, [ease, optOut]);
}
